import React from 'react';
import { useNavigate } from 'react-router-dom';

import ApartmentIcon from '@mui/icons-material/Apartment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import {
  Box,
  Grid,
  Link,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import ProjectStatusChip from 'components/chip/ProjectStatusChip';
import {
  EmptyHint,
  PagedWidgetList,
  StatTile,
  Widget,
  WidgetBarChart,
  WidgetGanttChart,
  WidgetRow,
  WidgetStackedBarChart,
} from 'components/dashboard/DashboardWidgets';
import DomainHero, { HERO_PALETTES } from 'components/dashboard/DomainHero';
import { ClientResult, listClient } from 'stores/ClientStoreAdapter';
import { ContractListResult, listContract } from 'stores/ContractStoreAdapter';
import { MasterTypeResult, getEmploymentType } from 'stores/MasterTypeStoreAdapter';
import { MemberListResult, listMember } from 'stores/MemberStoreAdapter';
import { PartnerResult, listPartner } from 'stores/PartnerStoreAdapter';
import { ProjectListResult, listProject } from 'stores/ProjectStoreAdapter';
import { listUserProfile, saveUserProfile } from 'stores/UserProfileStoreAdapter';
import { clampPage, mergeWithDefaults, parseProfileValue } from 'utils/UserProfileGuard';

const ACCENT = '#1565c0';

/** user_profile(AUTO) の profile_key */
const PROFILE_KEY = 'member-home-dashboard';

/** ダッシュボードの保存対象 state。形の正（未知キー無視・欠損はこの既定で補う） */
type DashboardState = {
  /** 「自分が課長のプロジェクト」の稼働中フィルタ（true=稼働中のみ / false=全て） */
  myProjectsActiveOnly: boolean;
  /** 「自分が課長のプロジェクト」リストの現在ページ（0始まり） */
  myProjectsPage: number;
  /** 「稼働中のプロジェクト」リストの現在ページ（0始まり） */
  activeProjectsPage: number;
};

const DEFAULT_DASHBOARD_STATE: DashboardState = {
  myProjectsActiveOnly: true,
  myProjectsPage: 0,
  activeProjectsPage: 0,
};

/**
 * 要員ドメインのトップ（ヒーロー + ウィジェットダッシュボード）。
 * 課長にとっての「自分が担当するプロジェクト」を最優先に据える。
 * - 自分が課長のプロジェクト（最優先・課長のみ意味を持つ）
 * - 稼働中プロジェクト
 * - KPI（プロジェクト / 要員 / パートナー / 取引先）
 * - 機能ナビ（要員 / パートナー / 取引先 / プロジェクト）
 */
const MemberHome: React.FC = () => {
  const navigate = useNavigate();
  const { userSession } = useAuth();
  const { userId } = userSession;

  // ID指定の詳細は必ず別タブで開く
  const openDetailInNewTab = (projectId: string) => {
    window.open(`/member/project/${projectId}`, '_blank', 'noopener,noreferrer');
  };

  const [projects, setProjects] = React.useState<ProjectListResult[]>([]);
  const [members, setMembers] = React.useState<MemberListResult[]>([]);
  const [partners, setPartners] = React.useState<PartnerResult[]>([]);
  const [clients, setClients] = React.useState<ClientResult[]>([]);
  const [employmentTypes, setEmploymentTypes] = React.useState<MasterTypeResult[]>([]);
  const [contracts, setContracts] = React.useState<ContractListResult[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  // ウィジェットの元データを一括取得する。初回ロードとヒーローの更新ボタンで共用。
  const reload = React.useCallback(async () => {
    try {
      const [p, m, pa, c, et, co] = await Promise.all([
        listProject({}).catch((): ProjectListResult[] => []),
        listMember({}).catch((): MemberListResult[] => []),
        listPartner({}).catch((): PartnerResult[] => []),
        listClient({}).catch((): ClientResult[] => []),
        getEmploymentType().catch((): MasterTypeResult[] => []),
        listContract({}).catch((): ContractListResult[] => []),
      ]);
      setProjects(p);
      setMembers(m);
      setPartners(pa);
      setClients(c);
      setEmploymentTypes(et);
      setContracts(co);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    reload();
  }, [reload]);

  // ヒーローの更新ボタン: 再取得中はスピナー表示にする
  const handleRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      await reload();
    } finally {
      setRefreshing(false);
    }
  }, [reload]);

  // --- ダッシュボード状態（user_profile AUTO で保存/復元）---
  // 「自分が課長のプロジェクト」の稼働状況フィルタ（デフォルト: 稼働中のみ）
  const [myProjectsActiveOnly, setMyProjectsActiveOnly] = React.useState(
    DEFAULT_DASHBOARD_STATE.myProjectsActiveOnly,
  );
  // 各 PagedWidgetList のページ（0始まり・controlled）
  const [myProjectsPage, setMyProjectsPage] = React.useState(
    DEFAULT_DASHBOARD_STATE.myProjectsPage,
  );
  const [activeProjectsPage, setActiveProjectsPage] = React.useState(
    DEFAULT_DASHBOARD_STATE.activeProjectsPage,
  );
  // 復元完了フラグ（復元前の初期値で保存し上書きしないためのガード）
  const [dashboardRestored, setDashboardRestored] = React.useState(false);

  // マウント時に user_profile(AUTO) からダッシュボード状態を復元する。
  // データ取得と並行してよい（ページはデータ件数確定後に PagedWidgetList 側でクランプされる）。
  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        const results = await listUserProfile(PROFILE_KEY, 'AUTO');
        if (!active) return;
        if (results.length > 0) {
          const restored = parseProfileValue<Partial<DashboardState>>(results[0].profileValue, {});
          const merged = mergeWithDefaults(DEFAULT_DASHBOARD_STATE, restored);
          setMyProjectsActiveOnly(merged.myProjectsActiveOnly);
          setMyProjectsPage(merged.myProjectsPage);
          setActiveProjectsPage(merged.activeProjectsPage);
        }
      } catch {
        // 復元失敗は既定値のまま（画面を壊さない）
      } finally {
        if (active) setDashboardRestored(true);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // 状態変化のたびに user_profile へ保存（復元完了後のみ）。API のためデバウンス。失敗は握りつぶす。
  React.useEffect(() => {
    if (!dashboardRestored) return;
    const state: DashboardState = { myProjectsActiveOnly, myProjectsPage, activeProjectsPage };
    const timer = setTimeout(() => {
      saveUserProfile({ profileKey: PROFILE_KEY, profileValue: JSON.stringify(state) }).catch(
        () => {
          // 保存失敗は UX を止めない
        },
      );
    }, 500);
    return () => clearTimeout(timer);
  }, [dashboardRestored, myProjectsActiveOnly, myProjectsPage, activeProjectsPage]);

  const myProjectsAll = React.useMemo(
    () => projects.filter((p) => p.managerId === userId),
    [projects, userId],
  );
  const myProjects = React.useMemo(
    () =>
      myProjectsActiveOnly ? myProjectsAll.filter((p) => p.status === 'ACTIVE') : myProjectsAll,
    [myProjectsAll, myProjectsActiveOnly],
  );
  const activeProjects = React.useMemo(
    () => projects.filter((p) => p.status === 'ACTIVE'),
    [projects],
  );
  const hasMyProjects = myProjectsAll.length > 0;

  // 課長別の担当プロジェクト数（稼働中）。バークリックでプロジェクト一覧を課長で絞り込む。
  const projectsByManager = React.useMemo(() => {
    const map = new Map<string, { name: string; count: number }>();
    activeProjects.forEach((p) => {
      if (!p.managerId) return;
      const entry = map.get(p.managerId) ?? { name: p.managerName || p.managerId, count: 0 };
      entry.count += 1;
      map.set(p.managerId, entry);
    });
    return Array.from(map.entries()).map(([id, v]) => ({ label: v.name, value: v.count, id }));
  }, [activeProjects]);
  const managerIdByLabel = (label: string) => projectsByManager.find((d) => d.label === label)?.id;

  // --- 取引先ごとの未完了プロジェクト数（status !== CLOSED）。バークリックで取引先絞り込み。---
  const projectsByClient = React.useMemo(() => {
    const map = new Map<string, { name: string; count: number }>();
    projects
      .filter((p) => p.status !== 'CLOSED')
      .forEach((p) => {
        if (!p.clientId) return;
        const entry = map.get(p.clientId) ?? { name: p.clientName || p.clientId, count: 0 };
        entry.count += 1;
        map.set(p.clientId, entry);
      });
    return Array.from(map.entries()).map(([id, v]) => ({ label: v.name, value: v.count, id }));
  }, [projects]);
  const clientIdByLabel = (label: string) => projectsByClient.find((d) => d.label === label)?.id;

  // --- 在籍要員（未削除）＋ 雇用形態ラベル解決 ---
  const activeMembers = React.useMemo(() => members.filter((m) => !m.isDeleted), [members]);
  const employmentLabel = React.useCallback(
    (id: string) => employmentTypes.find((t) => t.typeCode === id)?.typeLabel ?? id,
    [employmentTypes],
  );

  // グラフに出現する雇用形態の一覧（系列 ＝ 積み上げの層）。色は固定パレット。
  const EMPLOYMENT_PALETTE = React.useMemo(
    () => ['#1565c0', '#00838f', '#6a1b9a', '#2e7d32', '#ef6c00', '#c62828', '#5c6bc0', '#9e9e9e'],
    [],
  );
  const employmentTypeIds = React.useMemo(() => {
    const ids = new Set<string>();
    activeMembers.forEach((m) => ids.add(m.employmentTypeId || 'unknown'));
    return Array.from(ids);
  }, [activeMembers]);

  // memberId → employmentTypeId（契約ベースのグラフで雇用形態を引くため）
  const employmentTypeByMemberId = React.useMemo(() => {
    const map = new Map<string, string>();
    activeMembers.forEach((m) => map.set(m.memberId, m.employmentTypeId || 'unknown'));
    return map;
  }, [activeMembers]);

  /**
   * カテゴリ（groupId）× 雇用形態の積み上げ系列を組み立てる共通処理。
   * counts: groupId -> { name, byEmp: Map<empId, count> }
   */
  const buildEmploymentStack = React.useCallback(
    (groups: Map<string, { name: string; byEmp: Map<string, number> }>) => {
      const entries = Array.from(groups.entries());
      // 合計の多い順に並べる
      entries.sort((a, b) => {
        const sum = (x: Map<string, number>) => Array.from(x.values()).reduce((s, v) => s + v, 0);
        return sum(b[1].byEmp) - sum(a[1].byEmp);
      });
      const categories = entries.map(([, v]) => v.name);
      const ids = entries.map(([id]) => id);
      const series = employmentTypeIds.map((empId, i) => ({
        name: employmentLabel(empId),
        color: EMPLOYMENT_PALETTE[i % EMPLOYMENT_PALETTE.length],
        data: entries.map(([, v]) => v.byEmp.get(empId) ?? 0),
      }));
      return { categories, ids, series };
    },
    [employmentTypeIds, employmentLabel, EMPLOYMENT_PALETTE],
  );

  // --- パートナーごとの在籍要員数（雇用形態の積み上げ）。要員未削除 かつ 契約未終了パートナー。---
  const activePartnerIds = React.useMemo(
    () => new Set(partners.filter((p) => !p.contractEndDate).map((p) => p.partnerId)),
    [partners],
  );
  const membersByPartner = React.useMemo(() => {
    const map = new Map<string, { name: string; count: number }>();
    members
      .filter((m) => !m.isDeleted && m.partnerId && activePartnerIds.has(m.partnerId))
      .forEach((m) => {
        const id = m.partnerId as string;
        const entry = map.get(id) ?? { name: m.partnerName || id, count: 0 };
        entry.count += 1;
        map.set(id, entry);
      });
    return Array.from(map.entries()).map(([id, v]) => ({ label: v.name, value: v.count, id }));
  }, [members, activePartnerIds]);
  const partnerIdByLabel = (label: string) => membersByPartner.find((d) => d.label === label)?.id;

  // --- プロジェクトごとの要員数（契約ベース・重複排除、雇用形態の積み上げ）---
  const projectStack = React.useMemo(() => {
    // projectId -> name, memberIds(重複排除)
    const groups = new Map<string, { name: string; members: Map<string, string> }>();
    contracts.forEach((c) => {
      if (!c.projectId || !c.memberId) return;
      const g = groups.get(c.projectId) ?? {
        name: c.projectName || c.projectId,
        members: new Map<string, string>(),
      };
      // memberId -> employmentTypeId（在籍要員のみ雇用形態を持つ。無ければ unknown）
      g.members.set(c.memberId, employmentTypeByMemberId.get(c.memberId) ?? 'unknown');
      groups.set(c.projectId, g);
    });
    // members(Map) を byEmp 集計に変換
    const converted = new Map<string, { name: string; byEmp: Map<string, number> }>();
    groups.forEach((g, id) => {
      const byEmp = new Map<string, number>();
      g.members.forEach((emp) => byEmp.set(emp, (byEmp.get(emp) ?? 0) + 1));
      converted.set(id, { name: g.name, byEmp });
    });
    return buildEmploymentStack(converted);
  }, [contracts, employmentTypeByMemberId, buildEmploymentStack]);
  const projectIdByProjectLabel = (label: string) =>
    projectStack.ids[projectStack.categories.indexOf(label)];

  // --- 稼働中プロジェクトのガント（開始〜終了 or 現在）---
  const activeGantt = React.useMemo(() => {
    const now = Date.now();
    return activeProjects
      .filter((p) => p.startDate)
      .map((p) => {
        const start = new Date(p.startDate as string).getTime();
        const end = p.endDate ? new Date(p.endDate).getTime() : now;
        return {
          projectId: p.projectId,
          label: p.projectName,
          start,
          // 終了が開始より前になる異常データは、最低でも開始日+1日の幅を確保
          end: end > start ? end : start + 24 * 60 * 60 * 1000,
          color: ACCENT,
        };
      })
      .sort((a, b) => a.start - b.start);
  }, [activeProjects]);
  const projectIdByLabel = (label: string) => activeGantt.find((t) => t.label === label)?.projectId;

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
      <Stack spacing={2.5} sx={{ maxWidth: 1100 }}>
        {/* ヒーロー */}
        <DomainHero
          eyebrow="Resource Management"
          title="People, Projects & Partners"
          tagline="Manage the people, partners, clients, and projects that power your engagements — all connected in one place."
          palette={HERO_PALETTES.member}
          onBack={() => navigate('/')}
          onRefresh={handleRefresh}
          refreshing={refreshing}
        />

        {/* KPI ストリップ（最上部） */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<BusinessCenterIcon sx={{ fontSize: 16 }} />}
              label="プロジェクト"
              value={projects.length}
              suffix="件"
              color={ACCENT}
              loading={loading}
              onClick={() => navigate('/member/project')}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<BadgeOutlinedIcon sx={{ fontSize: 16 }} />}
              label="要員"
              value={members.length}
              suffix="名"
              color="#00838f"
              loading={loading}
              onClick={() => navigate('/member/member-mgmt')}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<HandshakeOutlinedIcon sx={{ fontSize: 16 }} />}
              label="パートナー"
              value={partners.length}
              suffix="社"
              color="#6a1b9a"
              loading={loading}
              onClick={() => navigate('/member/partner')}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<ApartmentIcon sx={{ fontSize: 16 }} />}
              label="取引先"
              value={clients.length}
              suffix="社"
              color="#283593"
              loading={loading}
              onClick={() => navigate('/member/client')}
            />
          </Grid>
        </Grid>

        {/* 自分が課長のプロジェクト / 稼働中プロジェクト（2カラム） */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              title="自分が課長のプロジェクト"
              action={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={myProjectsActiveOnly ? 'active' : 'all'}
                    onChange={(_e, v) => {
                      if (v !== null) setMyProjectsActiveOnly(v === 'active');
                    }}
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1,
                        py: 0.25,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                      },
                    }}
                  >
                    <ToggleButton value="active">稼働中</ToggleButton>
                    <ToggleButton value="all">全て</ToggleButton>
                  </ToggleButtonGroup>
                  <Link
                    component="button"
                    variant="caption"
                    underline="hover"
                    onClick={() =>
                      navigate(
                        `/member/project?managerId=self${myProjectsActiveOnly ? '&status=ACTIVE' : ''}`,
                      )
                    }
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                  >
                    プロジェクト管理
                    <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </Link>
                </Stack>
              }
            >
              {loading ? (
                <Stack spacing={1}>
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} variant="rounded" height={44} />
                  ))}
                </Stack>
              ) : !hasMyProjects ? (
                <EmptyHint text="自分が課長のプロジェクトはありません" />
              ) : myProjects.length === 0 ? (
                <EmptyHint text="稼働中のプロジェクトはありません" />
              ) : (
                <PagedWidgetList
                  items={myProjects}
                  keyOf={(p) => p.projectId}
                  page={clampPage(myProjectsPage + 1, Math.ceil(myProjects.length / 6)) - 1}
                  onPageChange={setMyProjectsPage}
                  renderItem={(p) => (
                    <WidgetRow accentColor={ACCENT} onClick={() => openDetailInNewTab(p.projectId)}>
                      <FolderSpecialIcon
                        sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {p.projectName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {p.clientName}
                        </Typography>
                      </Box>
                      <ProjectStatusChip status={p.status} />
                    </WidgetRow>
                  )}
                />
              )}
            </Widget>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              title="稼働中のプロジェクト"
              action={
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => navigate('/member/project?status=ACTIVE')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  すべて見る
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Link>
              }
            >
              {loading ? (
                <Stack spacing={1}>
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} variant="rounded" height={44} />
                  ))}
                </Stack>
              ) : activeProjects.length === 0 ? (
                <EmptyHint text="稼働中のプロジェクトはありません" />
              ) : (
                <PagedWidgetList
                  items={activeProjects}
                  keyOf={(p) => p.projectId}
                  page={clampPage(activeProjectsPage + 1, Math.ceil(activeProjects.length / 6)) - 1}
                  onPageChange={setActiveProjectsPage}
                  renderItem={(p) => (
                    <WidgetRow accentColor={ACCENT} onClick={() => openDetailInNewTab(p.projectId)}>
                      <WorkOutlineIcon
                        sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {p.projectName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {p.managerName}
                        </Typography>
                      </Box>
                      <ProjectStatusChip status={p.status} />
                    </WidgetRow>
                  )}
                />
              )}
            </Widget>
          </Grid>
        </Grid>

        {/* 課長別の担当プロジェクト数（稼働中）／ 取引先ごとの未完了プロジェクト数 */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              fit="content"
              title="課長別の担当プロジェクト数"
              action={
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => navigate('/member/project?status=ACTIVE')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  プロジェクト管理
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Link>
              }
            >
              {loading ? (
                <Skeleton variant="rounded" height={200} />
              ) : (
                <WidgetBarChart
                  data={projectsByManager}
                  color={ACCENT}
                  emptyText="稼働中のプロジェクトがありません"
                  onBarClick={(label) => {
                    const id = managerIdByLabel(label);
                    if (id) navigate(`/member/project?managerId=${id}&status=ACTIVE`);
                  }}
                />
              )}
            </Widget>
          </Grid>

          {/* 取引先ごとの未完了プロジェクト数 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              fit="content"
              title="取引先ごとの未完了プロジェクト数"
              action={
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => navigate('/member/client')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  取引先管理
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Link>
              }
            >
              {loading ? (
                <Skeleton variant="rounded" height={220} />
              ) : (
                <WidgetBarChart
                  data={projectsByClient}
                  color="#283593"
                  emptyText="未完了のプロジェクトがありません"
                  onBarClick={(label) => {
                    const id = clientIdByLabel(label);
                    if (id) navigate(`/member/project?clientId=${id}`);
                  }}
                />
              )}
            </Widget>
          </Grid>
        </Grid>

        {/* パートナーごとの在籍要員数 ／ プロジェクトごとの要員数 */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              fit="content"
              title="パートナーごとの在籍要員数"
              action={
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => navigate('/member/partner')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  パートナー管理
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Link>
              }
            >
              {loading ? (
                <Skeleton variant="rounded" height={220} />
              ) : (
                <WidgetBarChart
                  data={membersByPartner}
                  color="#6a1b9a"
                  emptyText="契約中のパートナーに紐づく要員がいません"
                  onBarClick={(label) => {
                    const id = partnerIdByLabel(label);
                    if (id) navigate(`/member/member-mgmt?partnerId=${id}`);
                  }}
                />
              )}
            </Widget>
          </Grid>

          {/* プロジェクトごとの要員数（契約ベース・雇用形態の積み上げ） */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              fit="content"
              title="プロジェクトごとの要員数"
              action={
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => navigate('/member/project')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  プロジェクト管理
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Link>
              }
            >
              {loading ? (
                <Skeleton variant="rounded" height={220} />
              ) : (
                <WidgetStackedBarChart
                  categories={projectStack.categories}
                  series={projectStack.series}
                  unit="名"
                  emptyText="契約に紐づく要員がいません"
                  onBarClick={(label) => {
                    // プロジェクトを条件に、その要員（契約）一覧を検索する
                    const id = projectIdByProjectLabel(label);
                    if (id) navigate(`/contract/contract-list?projectId=${id}`);
                  }}
                />
              )}
            </Widget>
          </Grid>
        </Grid>

        {/* 稼働中プロジェクトのガントチャート */}
        <Widget
          title="稼働中プロジェクトのタイムライン"
          action={
            <Link
              component="button"
              variant="caption"
              underline="hover"
              onClick={() => navigate('/member/project?status=ACTIVE')}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
            >
              プロジェクト管理
              <ArrowForwardIcon sx={{ fontSize: 14 }} />
            </Link>
          }
        >
          {loading ? (
            <Skeleton variant="rounded" height={240} />
          ) : (
            <WidgetGanttChart
              tasks={activeGantt}
              emptyText="開始日の設定された稼働中プロジェクトがありません"
              onBarClick={(label) => {
                const id = projectIdByLabel(label);
                if (id) openDetailInNewTab(id);
              }}
            />
          )}
        </Widget>
      </Stack>
    </Box>
  );
};

export default MemberHome;
